import type { BlinkEvent, PointerInputEvent, SuppressionLevel } from "../core/types.ts";
import { FrameClock, type Mechanic, type Readout, relief } from "./mechanic.ts";
import { RhythmMeter } from "./rhythm.ts";
import { DEFAULT_TUNING, type KartTuning } from "./tuning.ts";

/**
 * A first-person kart, steered with one thumb.
 *
 * Put your thumb anywhere and drag: left and right steer, dragging back
 * brakes. There is no accelerator, because there is nothing to decide about
 * going forward — you are always going forward. The only questions are where
 * you are pointed and what is slowing you down.
 *
 * ## Why suppression is drag
 *
 * Every other mechanic had to invent a vocabulary for its penalty. This one
 * already has one: "slow" is what braking means, so suppression borrows it
 * rather than adding a second language. Stop blinking and the kart bleeds
 * speed exactly as though you were dragging the brake — same readout, same
 * feel, no explanation needed.
 *
 * That keeps design doc §1 intact. Nothing scores a blink; blinking at a
 * natural rate simply means nothing is holding you back, which is what "you
 * never think about your eyes" looks like when it is working. And the drag is
 * bounded well short of a stop, because a game that halts is a game you put
 * down, and someone who has stopped moving has stopped receiving the lesson.
 *
 * ## Why the road curves
 *
 * Steering with no road to hold would be cosmetic. The track wanders on a
 * deterministic curve; drifting off it costs speed. So the thumb has a
 * continuous job at exactly the frequency design doc §2 asks the thumb to work
 * at, and the eye channel stays low-frequency and high-impact.
 */

/**
 * The constants moved to `tuning.ts`, and the arguments moved with them.
 *
 * They are still constants in every sense that matters — `DEFAULT_TUNING` holds
 * the shipped values and every test asserts against it. What changed is that
 * they are now addressable, so `/tune` can put a slider on one and someone can
 * feel what it does in a second instead of editing, rebuilding and re-recording.
 *
 * Re-exported individually because tests and `slice/` read them by name, and
 * because a value with a name is easier to argue about than a property access.
 */
const {
  baseSpeed: BASE_SPEED,
  fullBrakeFraction: FULL_BRAKE_FRACTION,
  fullLockFraction: FULL_LOCK_FRACTION,
  jumpMs: JUMP_MS,
  maxBrake: MAX_BRAKE,
  maxSuppressionAirLoss: MAX_SUPPRESSION_AIR_LOSS,
  maxSuppressionDrag: MAX_SUPPRESSION_DRAG,
  obstacleSpacing: OBSTACLE_SPACING,
  stunDrag: STUN_DRAG,
} = DEFAULT_TUNING;

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/**
 * Where the track centre sits at a given distance, in track-widths.
 *
 * Deterministic, like Balance's drift and for the same reasons: it has to feel
 * unpredictable over ninety seconds while replaying identically, or a recorded
 * session cannot be re-driven and a test cannot assert a position.
 */
function trackCentre(distance: number): number {
  return Math.sin(distance / 190) * 0.55 + Math.sin(distance / 63) * 0.3;
}

/**
 * Where obstacle `n` sits, and whether it can be steered around at all.
 *
 * Deterministic for the same reason the track is: a recorded session has to be
 * re-drivable and a test has to be able to assert a collision.
 *
 * Every third one spans the lane, so the jump is the only answer to it. The
 * rest sit off to one side, where steering is also an answer. Two verbs, both
 * legitimate, and neither made redundant by the other — an obstacle course that
 * only ever needs one input teaches the player to stop using the other.
 */
export function obstacleAt(
  n: number,
  spacing = OBSTACLE_SPACING,
): { distance: number; lateral: number; full: boolean } {
  // From one spacing in, never zero: an obstacle at distance 0 sits on the
  // starting line and charges a hit before the player has touched anything.
  const distance = (n + 1) * spacing;
  const full = n % 3 === 0;
  // -1, 0, +1 by index, scaled to land inside the lane rather than on its edge.
  const side = ((n % 3) - 1) * 0.5;
  return { distance, lateral: trackCentre(distance) + side, full };
}

export class Kart implements Mechanic {
  readonly id = "kart" as const;
  readonly hint = "drag to steer · pull back to brake · blink to keep your speed";

  #clock = new FrameClock();
  #steer = 0;
  #brake = 0;
  #suppression = 0;
  #lateral = 0;
  #distance = 0;
  #anchor: { x: number; y: number } | null = null;
  /** Milliseconds of air left. Zero means on the ground. */
  #airMs = 0;
  /** Height this jump was launched with, 0..1 — fixed at takeoff. */
  #airPower = 0;
  #stunMs = 0;
  #hits = 0;
  #rhythm = new RhythmMeter();
  /**
   * Blinks waiting for a frame to be resolved against.
   *
   * Events carry FRAME time and `update` is called with `performance.now()`;
   * they differ by tens of seconds on a real page, and `mechanic.ts` carries a
   * long comment about the bug that rule was written after. So a blink is not
   * timed when it arrives — it is queued, and stamped with the clock `update`
   * is given. Two blinks landing in one frame then measure an interval of zero,
   * which is exactly what the second one deserves.
   */
  #pendingBlinks: number[] = [];
  /** Index of the next obstacle not yet passed. */
  #nextObstacle = 0;

  #t: KartTuning = DEFAULT_TUNING;

  constructor(
    private readonly road: HTMLElement,
    private readonly arrow: HTMLElement,
    private readonly obstacle?: HTMLElement,
    tuning: KartTuning = DEFAULT_TUNING,
  ) {
    this.#t = tuning;
  }

  /**
   * Swaps the constants mid-session.
   *
   * `/tune` needs this — the whole value of a tuning bench is that a slider
   * moves and the road answers on the next frame. It deliberately does not
   * reset position or distance: watching the same corner at two settings is
   * the comparison worth making.
   */
  tune(tuning: KartTuning): void {
    this.#t = tuning;
  }

  get tuning(): KartTuning {
    return this.#t;
  }

  /**
   * Leaves the ground, with whatever height suppression has left you.
   *
   * Power is locked at takeoff rather than sampled during the arc: a jump whose
   * height changed mid-air because the player blinked would be unreadable, and
   * the lesson lands better as "I was heavy when I jumped" than as a wobble.
   *
   * @returns true if the kart actually left the ground.
   */
  jump(): boolean {
    if (this.#airMs > 0) return false;
    this.#airPower = 1 - this.#suppression * this.#t.maxSuppressionAirLoss;
    this.#airMs = this.#t.jumpMs * this.#airPower;
    return true;
  }

  /** 0 on the ground, rising to 1 at the top of the arc. */
  get height(): number {
    if (this.#airMs <= 0) return 0;
    const span = this.#t.jumpMs * this.#airPower;
    if (span <= 0) return 0;
    // Sine arc rather than a parabola: it leaves the ground and lands softly at
    // the same rate, which reads as a hop instead of a launch.
    return Math.sin((1 - this.#airMs / span) * Math.PI) * this.#airPower;
  }

  get airborne(): boolean {
    return this.#airMs > 0;
  }

  get hits(): number {
    return this.#hits;
  }

  onSuppression(level: SuppressionLevel): void {
    this.#suppression = level.value;
  }

  /**
   * The reward channel. See `rhythm.ts` for why it pays rhythm, not blinks.
   *
   * Scaled by how far the lid closed, per design doc §5 — a half blink is worth
   * half the credit, which teaches closing properly with no copy at all.
   */
  onBlink(event: BlinkEvent): void {
    this.#pendingBlinks.push(relief(event.amplitude));
  }

  /** 0..1. How full the rhythm meter is — the turbo the player has earned. */
  get rhythm(): number {
    return this.#rhythm.value;
  }

  /**
   * The gesture, not the landing point.
   *
   * Steering is measured from wherever the thumb went down, so there is no
   * correct place to put it and no need to look. That matters more than it
   * sounds: measured across real sessions, every labelling tap landed on the
   * same half of the screen, because a thumb has a resting position and does
   * not want to leave it. An absolute control would have fought that habit;
   * a relative one does not care where the habit is.
   */
  onPointer(event: PointerInputEvent): void {
    if (event.phase === "down") {
      this.#anchor = { x: event.x, y: event.y };
      return;
    }
    if (event.phase === "up") {
      this.#anchor = null;
      return;
    }
    const anchor = this.#anchor;
    if (!anchor) return;

    this.#steer = clamp((event.x - anchor.x) / this.#t.fullLockFraction, -1, 1);
    // Only backwards counts. Pushing forward does nothing, because there is no
    // faster than forward.
    this.#brake = clamp01((event.y - anchor.y) / this.#t.fullBrakeFraction);
  }

  update(t: number): void {
    const dt = this.#clock.step(t) / 1000;

    // A released wheel returns to centre rather than holding its last angle,
    // which is what stops a lifted thumb from reading as a stuck control.
    if (!this.#anchor) {
      const decay = Math.min(1, this.#t.centringPerS * dt);
      this.#steer -= this.#steer * decay;
      this.#brake -= this.#brake * decay;
    }

    this.#airMs = Math.max(0, this.#airMs - dt * 1000);
    this.#stunMs = Math.max(0, this.#stunMs - dt * 1000);

    for (const r of this.#pendingBlinks) this.#rhythm.blink(t, r);
    this.#pendingBlinks.length = 0;
    this.#rhythm.tick(t);

    this.#lateral = clamp(this.#lateral + this.#steer * this.#t.lateralPerS * dt, -2, 2);
    const offset = this.#lateral - trackCentre(this.#distance);
    const offroad = Math.abs(offset) > 1;

    const speed = this.speedFactor(offroad);
    const from = this.#distance;
    this.#distance += this.#t.baseSpeed * speed * dt;
    this.#collide(from, this.#distance);

    // Scrolling the road texture IS the speed cue — the number in the HUD only
    // confirms what the ground is already saying.
    this.road.style.backgroundPositionY = `${this.#distance.toFixed(1)}px`;
    // The lateral shift is in the plane's own coordinates, before the 3D tilt —
    // the individual `rotate` property composes after `transform`, so this
    // slides the road across itself rather than across the screen. The plane is
    // twice the width of its window, so a percentage here is half as large on
    // screen as it reads.
    this.road.style.transform = `translateX(${(-offset * 9).toFixed(2)}%) rotate(${(-this.#steer * 2).toFixed(2)}deg)`;
    this.road.style.opacity = offroad ? "0.55" : "";

    // Steering is read almost entirely off this, so it gets the wider swing.
    // The lift is in screen space and belongs to the arrow alone: the road is
    // rotated 66° about its own base, so a translateY there would slide it
    // along the surface — forward, not up — and read as a lurch rather than a
    // hop.
    const lift = this.height * 26;
    this.arrow.style.transform =
      `translateY(${(-lift).toFixed(1)}px) ` +
      `rotate(${(this.#steer * 45).toFixed(1)}deg) ` +
      `scaleY(${(0.55 + speed * 0.45).toFixed(2)})`;
    this.arrow.style.opacity = (0.45 + speed * 0.55).toFixed(2);

    this.#paintObstacle();
  }

  /**
   * Draws the next obstacle, if this Kart was given somewhere to draw it.
   *
   * Optional because `kart.test.ts` builds a Kart from two five-line fakes, and
   * a third required element would make every existing test construct scenery
   * it does not care about.
   */
  #paintObstacle(): void {
    const target = this.obstacle;
    if (!target) return;

    const next = obstacleAt(this.#nextObstacle, this.#t.obstacleSpacing);
    const ahead = next.distance - this.#distance;
    // Beyond this it is a sub-pixel speck at the vanishing point.
    if (ahead > this.#t.obstacleSpacing || ahead < 0) {
      target.style.opacity = "0";
      return;
    }

    // 0 at the horizon, 1 underfoot. The same 1/(1+z) falloff the CSS
    // perspective applies to the road, so the two agree about where "far" is.
    const near = 1 / (1 + ahead / 22);
    const lateral = next.lateral - trackCentre(this.#distance);
    target.style.opacity = String(Math.min(1, near * 2.2));
    // `bottom` as a percentage of the world walks it from the horizon (42%,
    // where the road converges) down to the near edge. Percentages, never
    // viewport units: /themes renders this same element inside a tile.
    target.style.bottom = `${(42 - near * 34).toFixed(1)}%`;
    target.style.transform = `translateX(${(lateral * 100).toFixed(1)}%) scale(${(0.3 + near * 0.8).toFixed(3)})`;
    target.dataset.full = next.full ? "true" : "false";
  }

  /** Charges any obstacle crossed between two distances. */
  #collide(from: number, to: number): void {
    while (obstacleAt(this.#nextObstacle, this.#t.obstacleSpacing).distance <= to) {
      const hit = obstacleAt(this.#nextObstacle, this.#t.obstacleSpacing);
      this.#nextObstacle++;
      if (hit.distance < from) continue;

      // Airborne clears everything — that is what the jump is for.
      if (this.#airMs > 0) continue;
      // A partial obstacle can also be steered around. A full one cannot, which
      // is what makes the jump necessary rather than decorative.
      if (!hit.full && Math.abs(this.#lateral - hit.lateral) > this.#t.obstacleHalfWidth) continue;

      this.#hits++;
      this.#stunMs = this.#t.stunMs;
    }
  }

  /**
   * What the kart is actually doing, as a multiple of base speed.
   *
   * Two halves, and they are kept apart on purpose. The penalties multiply into
   * a 0..1 pool — braking, suppression, off-track, hits — and the reward sits
   * outside it as a bonus that can push the result past 1. Folding the turbo
   * into the same clamp would have made a full rhythm meter invisible to anyone
   * already at full speed, which is precisely the player it should be rewarding.
   *
   * So 100% in the HUD is "nothing is holding you back" and 150% is "and you
   * have been blinking properly". The second number is the one this game never
   * had before.
   */
  private speedFactor(offroad: boolean): number {
    const penalties = clamp01(
      (1 - this.#brake * this.#t.maxBrake) *
        (1 - this.#suppression * this.#t.maxSuppressionDrag) *
        (offroad ? 1 - this.#t.offroadDrag : 1) *
        (this.#stunMs > 0 ? this.#t.stunDrag : 1),
    );
    return penalties * (1 + this.#t.turboGain * this.#rhythm.value);
  }

  readout(): Readout {
    const offset = this.#lateral - trackCentre(this.#distance);
    const speed = this.speedFactor(Math.abs(offset) > 1);
    return {
      value: `${Math.round(this.#distance)}m`,
      label: `speed ${Math.round(speed * 100)}%`,
      ...(speed < 0.7 ? { alert: true } : {}),
    };
  }

  get stunned(): boolean {
    return this.#stunMs > 0;
  }

  get distance(): number {
    return this.#distance;
  }

  get steer(): number {
    return this.#steer;
  }

  get brake(): number {
    return this.#brake;
  }

  /** How far off the track centre, in track-widths. Past 1 is off the road. */
  get offset(): number {
    return this.#lateral - trackCentre(this.#distance);
  }

  get speed(): number {
    return this.speedFactor(Math.abs(this.offset) > 1);
  }

  reset(): void {
    this.#clock.reset();
    this.#steer = 0;
    this.#brake = 0;
    this.#suppression = 0;
    this.#lateral = 0;
    this.#distance = 0;
    this.#anchor = null;
    this.#airMs = 0;
    this.#airPower = 0;
    this.#stunMs = 0;
    this.#hits = 0;
    this.#nextObstacle = 0;
    this.#rhythm.reset();
    this.#pendingBlinks.length = 0;
    this.road.style.backgroundPositionY = "";
    this.road.style.transform = "";
    this.road.style.opacity = "";
    this.arrow.style.transform = "";
    this.arrow.style.opacity = "";
    if (this.obstacle) {
      this.obstacle.style.opacity = "";
      this.obstacle.style.transform = "";
      this.obstacle.style.bottom = "";
    }
  }
}

export {
  BASE_SPEED,
  FULL_BRAKE_FRACTION,
  FULL_LOCK_FRACTION,
  JUMP_MS,
  MAX_BRAKE,
  MAX_SUPPRESSION_AIR_LOSS,
  MAX_SUPPRESSION_DRAG,
  OBSTACLE_SPACING,
  STUN_DRAG,
};
